import { HttpError } from '../../lib/errors';
import Error5xx from '../../lib/screens/error-5xx';
import Error403 from '../../lib/screens/error-403';
import Error400 from '../../lib/screens/error-400';
import Error401Modal from '../../lib/components/error-401-modal';
import HttpStatusCodes from 'http-status-codes';
import { parseErrorEnvelope, getMessageForErrorCode, ERROR_CODE_MESSAGES } from '../utils/error-body';
import { captureException } from '@frontend/feature-monitor';
import { ErrorBoundary } from 'react-error-boundary';
import PropTypes from 'prop-types';
import { createContext, useContext } from 'react';
import { useLocation } from 'react-router-dom';

const HelpUrlContext = createContext(undefined);
// Code→message map used to resolve 422 envelopes. Defaults to the lib's EMPTY
// map (codes are not globally unique across backends); each app injects its own,
// scoped to its backend namespace, via `errorCodeMessages` on `ErrorBoundaryWrapper`.
const ErrorCodeMessagesContext = createContext(ERROR_CODE_MESSAGES);

/**
 * Returns the appropriate error component based on the HTTP status code.
 * @param {number} statusCode - The HTTP status code of the error.
 * @param {Error} error - The error object.
 * @param {string} helpUrl optional - The URL to the help center.
 * @param {Object} errorCodeMessages - The code→message map for 422 envelopes.
 * @returns {React.Component} - The corresponding React component for the error.
 */
function getErrorHttpComponent(statusCode, error, helpUrl, errorCodeMessages) {
  if (statusCode >= 500 && statusCode <= 599) {
    captureException(error);
  }
  switch (statusCode) {
    case HttpStatusCodes.UNAUTHORIZED:
      return <Error401Modal open={true} />
    case HttpStatusCodes.FORBIDDEN:
      return <Error403 />
    case HttpStatusCodes.BAD_REQUEST:
      return <Error400 />
    case HttpStatusCodes.UNPROCESSABLE_ENTITY: {
      // The backend returns 422 with a JSON envelope `{ error: { code, description } }`
      // for known, user-facing failures (e.g. a process that finished with errors).
      // These are expected business outcomes, not system faults, so they are NOT sent
      // to Sentry (captureException only fires for the 500-599 range above).
      // Render the generic 5xx layout but swap the body for the code-specific message.
      // "Try again" is hidden because retrying cannot change the outcome.
      // Unknown/unparseable codes fall through to the generic message (with retry).
      const envelope = parseErrorEnvelope(error?.message);
      const specificMessage = envelope && getMessageForErrorCode(envelope.code, errorCodeMessages);
      if (specificMessage) {
        // `messageFallback` is only used when the i18n key does not resolve.
        // Prefer our versioned/reviewable fallback over the backend's raw
        // `description` (which is outside this repo's control and untranslated),
        // so a consumer missing the key never leaks raw backend text to the UI.
        return (
          <Error5xx
            helpUrl={helpUrl}
            messageKey={specificMessage.key}
            messageFallback={specificMessage.fallback || envelope.description}
            showTryAgain={false}
          />
        );
      }
      return <Error5xx helpUrl={helpUrl} />;
    }
    default:
      return <Error5xx helpUrl={helpUrl} />;
  }
}

function ErrorFallback({ error }) {
  const helpUrl = useContext(HelpUrlContext);
  const errorCodeMessages = useContext(ErrorCodeMessagesContext);
  if (error?.status && error instanceof HttpError) {
    return getErrorHttpComponent(error.status, error, helpUrl, errorCodeMessages);
  }
  captureException(error);
  return <Error5xx helpUrl={helpUrl} />;
}

ErrorFallback.propTypes = {
  error: PropTypes.instanceOf(Error),
};

const ErrorBoundaryWrapper = ({ children, enableErrorBoundary, helpUrl, errorCodeMessages }) => {
  const location = useLocation();
  const inheritedHelpUrl = useContext(HelpUrlContext);
  const effectiveHelpUrl = helpUrl ?? inheritedHelpUrl;

  const inheritedErrorCodeMessages = useContext(ErrorCodeMessagesContext);
  const effectiveErrorCodeMessages = errorCodeMessages ?? inheritedErrorCodeMessages;

  const boundary = enableErrorBoundary ? (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      resetKeys={[location.pathname]}
    >
      {children}
    </ErrorBoundary>
  ) : children;

  return (
    <HelpUrlContext.Provider value={effectiveHelpUrl}>
      <ErrorCodeMessagesContext.Provider value={effectiveErrorCodeMessages}>
        {boundary}
      </ErrorCodeMessagesContext.Provider>
    </HelpUrlContext.Provider>
  );
};

ErrorBoundaryWrapper.propTypes = {
  children: PropTypes.element,
  enableErrorBoundary: PropTypes.bool,
  helpUrl: PropTypes.string,
  // Optional per-app override of the 422 code→message map, so error codes are
  // scoped to the consuming app's backend namespace instead of a lib-global map.
  errorCodeMessages: PropTypes.object,
};

export default ErrorBoundaryWrapper;
